"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@clerk/nextjs";
import { ActivityIcon, ArrowLeftIcon, Building2, CloudSunIcon, DnaIcon, DropletsIcon, FlagIcon, GitMergeIcon, Landmark, LandPlotIcon, LayoutDashboardIcon, LifeBuoyIcon, LeafIcon, Newspaper, PackageIcon, RefreshCwIcon, ShieldCheckIcon, ShieldIcon, SproutIcon, UserCogIcon, UsersIcon, WheatIcon, WrenchIcon } from "lucide-react";
import type { Route } from "next";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import * as React from "react";
import { Suspense } from "react";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, Sidebar, useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { SharedSidebar, type SharedNavGroup } from "../app-sidebar/shared-sidebar";
const adminNavGroups: SharedNavGroup[] = [{
  items: [{
    title: "Dashboard",
    url: "/admin",
    icon: LayoutDashboardIcon,
    routeCheck: "strict"
  }, {
    title: "Suporte",
    url: "/admin/support",
    icon: LifeBuoyIcon,
    routeCheck: "loose"
  }, {
    title: "Usuários da plataforma",
    url: "/admin/platform-users",
    icon: UserCogIcon,
    routeCheck: "loose"
  }, {
    title: "Organizações",
    url: "/admin/organizations",
    icon: Building2,
    routeCheck: "loose"
  }, {
    title: "Feature flags",
    url: "/admin/feature-flags",
    icon: FlagIcon,
    routeCheck: "loose"
  }, {
    title: "Notícias",
    url: "/admin/news",
    icon: Newspaper,
    routeCheck: "loose"
  }, {
    title: "Legislação Agro",
    url: "/admin/official-agro",
    icon: Landmark,
    routeCheck: "loose"
  }, {
    title: "Atividades",
    url: "/admin/activities",
    icon: ActivityIcon,
    routeCheck: "loose"
  }, {
    title: "Espécies",
    url: "/admin/species",
    icon: DnaIcon,
    routeCheck: "loose"
  }, {
    title: "Variedades de culturas",
    url: "/admin/crop-varieties",
    icon: LeafIcon,
    routeCheck: "loose"
  }]
}, {
  label: "Projetos",
  collapsible: true,
  defaultOpen: true,
  items: [{
    title: "Bancos",
    url: "/admin/banks",
    icon: Building2,
    routeCheck: "loose"
  }, {
    title: "Classificações de produtor",
    url: "/admin/project-producer-classifications",
    icon: UsersIcon,
    routeCheck: "loose"
  }, {
    title: "Destinações da área",
    url: "/admin/producer-rural-property-area-destinations",
    icon: LandPlotIcon,
    routeCheck: "loose"
  }, {
    title: "Tipos de seguro",
    url: "/admin/project-insurance-types",
    icon: ShieldIcon,
    routeCheck: "loose"
  }, {
    title: "Modalidades de seguro",
    url: "/admin/project-insurance-modalities",
    icon: ShieldCheckIcon,
    routeCheck: "loose"
  }, {
    title: "Grupos de insumos",
    url: "/admin/project-supply-groups",
    icon: PackageIcon,
    routeCheck: "loose"
  }, {
    title: "Grupos de serviços",
    url: "/admin/project-service-groups",
    icon: WrenchIcon,
    routeCheck: "loose"
  }, {
    title: "Tipos de agricultura",
    url: "/admin/project-agriculture-types",
    icon: SproutIcon,
    routeCheck: "loose"
  }, {
    title: "Tipos de integração/consórcio",
    url: "/admin/project-integration-consortium-types",
    icon: GitMergeIcon,
    routeCheck: "loose"
  }, {
    title: "Grãos/sementes",
    url: "/admin/project-grain-seeds",
    icon: WheatIcon,
    routeCheck: "loose"
  }, {
    title: "Tipos de clima",
    url: "/admin/project-climate-types",
    icon: CloudSunIcon,
    routeCheck: "loose"
  }, {
    title: "Tipos de irrigação",
    url: "/admin/project-irrigation-types",
    icon: DropletsIcon,
    routeCheck: "loose"
  }, {
    title: "Tipos de cultivo",
    url: "/admin/project-cultivation-types",
    icon: LeafIcon,
    routeCheck: "loose"
  }, {
    title: "Ciclos de produção / fases da criação",
    url: "/admin/project-production-cycles",
    icon: RefreshCwIcon,
    routeCheck: "loose"
  }, {
    title: "Manejo e solo",
    url: "/admin/soil-input-management",
    icon: LandPlotIcon,
    routeCheck: "loose"
  }]
}];
function BackToAppLink() {
  const $ = _c(13);
  const {
    orgSlug
  } = useAuth();
  const searchParams = useSearchParams();
  const {
    isMobile,
    state
  } = useSidebar();
  const isCollapsed = state === "collapsed" && !isMobile;
  const fallbackUrl = orgSlug ? `/${orgSlug}` : "/";
  let t0;
  if ($[0] !== fallbackUrl || $[1] !== searchParams) {
    const returnTo = searchParams.get("returnTo");
    t0 = returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//") && !returnTo.startsWith("/admin") ? returnTo : fallbackUrl;
    $[0] = fallbackUrl;
    $[1] = searchParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const backToAppUrl = t0;
  const t1 = backToAppUrl as Route;
  let t2;
  if ($[3] !== t1) {
    t2 = <Link href={t1} />;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArrowLeftIcon className="size-4" />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = isCollapsed && "sr-only";
  let t5;
  if ($[6] !== t4) {
    t5 = cn(t4);
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <span className={t5}>Voltar ao app</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t6) {
    t7 = <SidebarMenu><SidebarMenuItem><SidebarMenuButton render={t2} tooltip="Voltar ao app" className="text-muted-foreground hover:bg-muted/50 hover:text-foreground h-10 rounded-lg group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0">{t3}{t6}</SidebarMenuButton></SidebarMenuItem></SidebarMenu>;
    $[10] = t2;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
export function AdminSidebar(t0) {
  const $ = _c(5);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Suspense><BackToAppLink /></Suspense>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props) {
    t2 = <SharedSidebar {...props} footerTopContent={t1} matchMode="absolute" routes={adminNavGroups} />;
    $[3] = props;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}